import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {combineLatest, debounceTime, filter, Subscription, take} from 'rxjs';
import {selectDropdownLoaded, selectDropdownOptions,} from '../../../../store/selectors/dropdowns.selector';
import {CaseAddressComponent} from './components/case-address/case-address.component';
import {selectAddressInformations} from '../../../../store/selectors/address-informations.selector';
import {selectBlackList, selectPatient, selectReadonly,} from '../../../../store/selectors/common-case.selector';
import {RegionDistrictService} from '../../../../../definition/region/service/region-district.service';
import {ICaseAddressInformations, ICasePatient, ICasePatientAddress} from '../../../../interfaces/interfaces';

@Component({
  selector: 'app-address-informations',
  templateUrl: './address-informations.component.html',
  styleUrl: './address-informations.component.scss',
})
export class AddressInformationsComponent implements OnInit, OnDestroy {
  form: FormGroup;
  private subscription!: Subscription;
  @Output() formChanged = new EventEmitter();
  @ViewChild('pickupAddress') pickupAddress: CaseAddressComponent;
  @ViewChild('dropoffAddress') dropoffAddress: CaseAddressComponent;
  defaultAddress = {
    city: '',
    country: '',
    district: '',
    formattedAddress: '',
    isManuelAddress: false,
    latitude: null,
    longitude: null,
    postalCode: '',
    street: '',
  };


  institutions: { address: any; label: string; value: any }[];
  blackList: string[] = [];
  hiddenFields: { [key: string]: boolean } = {}; // Gizlenmesi gereken alanlar
  bridgeInfos: {
    isBrigdePass: boolean;
    regionNumber: number | null;
    description: string;
    fromAddressZone: string;
    toAddressZone: string;
  } | null;

  isReadOnly: boolean = false;
  readonlyInformations: ICaseAddressInformations | null;
  private formSubscriptions = new Subscription();
  patient:ICasePatient;

  constructor(
    private readonly store: Store,
    private readonly fb: FormBuilder,
    private readonly regionDistrictService: RegionDistrictService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      pickupInstitution: new FormControl(null),
      pickupAddress: new FormControl({...this.defaultAddress}),
      dropoffInstitution: new FormControl(null),
      dropoffAddress: new FormControl({...this.defaultAddress}),
      dropoffInstitutionApproval: new FormControl(null),
      dropoffInstitutionDescription: new FormControl(null),
    });

    this.listenForm();

    combineLatest([
      this.store.pipe(select(selectDropdownOptions('institutions'))),
      this.store.pipe(select(selectDropdownLoaded)),
    ])
      .pipe(
        // loaded flag'ı true olduğunda (yani veriler yüklendiğinde) veri akışı devam eder
        filter(([institutions, loaded]) => loaded),
        take(1)
      )
      .subscribe(([institutions, loaded]) => {
        this.institutions = institutions;
      });

    this.subscription = combineLatest([
      this.store.select(selectAddressInformations),
      this.store.select(selectBlackList),
      this.store.select(selectReadonly),
      this.store.select(selectPatient)
    ]).subscribe({
      next: ([addressInformations, blackList, isReadOnly,selectPatient]) => {
        this.blackList = blackList;
        this.readonlyInformations = addressInformations
          ? {...addressInformations}
          : null;
        this.isReadOnly = isReadOnly;
        this.patient=selectPatient;
        this.updateHiddenFields();  
        
        this.form.patchValue({
          pickupInstitution: addressInformations?.pickupInstitution?.value,
          pickupAddress: this.pickupAddressDecider(addressInformations?.pickupAddress),
          dropoffInstitution: addressInformations?.dropoffInstitution?.value,
          dropoffAddress: addressInformations?.dropoffAddress,
          dropoffInstitutionApproval:
          addressInformations?.dropoffInstitutionApproval,
          dropoffInstitutionDescription:
          addressInformations?.dropoffInstitutionDescription,
        });

        setTimeout(() => {
          this.pickupAddress?.setAddress(this.pickupAddressDecider(addressInformations?.pickupAddress), true);
          this.dropoffAddress?.setAddress(addressInformations?.dropoffAddress, true);
          
          const pickup = this.form.get('pickupAddress')?.value;
          const dropoff = this.form.get('dropoffAddress')?.value;
          if (
            pickup?.city &&
            pickup?.district &&
            dropoff?.city &&
            dropoff?.district &&
            !this.hiddenFields['dropoffAddress']
          ) {
            this.calculateBridgeInfos(pickup, dropoff);
          }
        }, 200);
        this.form.markAsPristine();
      },
    });
  }

  private calculateBridgeInfos(pickup: any, dropoff: any) {
    this.regionDistrictService
      .calculateRegionBetweenTwoDistricts({
        fromCity: pickup.city,
        fromDistrict: pickup.district,
        toCity: dropoff.city,
        toDisctrict: dropoff.district,
      })
      .subscribe({
        next: (res) => {
          this.bridgeInfos = res;
          this.cdr.detectChanges();
        },
        error: () => {
          this.bridgeInfos = null;
        },
      });
  }


  pickupAddressDecider(address: any) {
    if(address) {
      return address;
    }
    const priorityAddress = this.patient?.addresses?.find( (addr: ICasePatientAddress) => addr.isPriority);
    if(priorityAddress) {
      return {
        city: priorityAddress.city,
        country: priorityAddress.country,
        district: priorityAddress.district,
        formattedAddress: priorityAddress.formattedAddress,
        isManuelAddress: false,
        latitude: priorityAddress.latitude,
        longitude: priorityAddress.longitude,
        postalCode: priorityAddress.postalCode,
        street: priorityAddress.street,
      }
    }
    return address;
  }

  listenForm() {
    if (this.formSubscriptions) {
      this.formSubscriptions.unsubscribe();
    }
    this.formSubscriptions = new Subscription();

    this.formSubscriptions.add(
      this.form.valueChanges.pipe(debounceTime(300)).subscribe((res) => {
        if (this.form.dirty) {
          this.formChanged.emit();
        }
      })
    );
  }

  handleSetAddress(address: any, type: 'dropoff' | 'pickup') {
    const pickupAddress = this.form.get('pickupAddress')?.value;
    const dropoffAddress = this.form.get('dropoffAddress')?.value;
    switch (type) {
      case 'pickup':
        this.form.patchValue({
          pickupAddress: {
            ...address,
          },
        });
        if (address?.markAsPristine) {
          setTimeout(() => {
            this.form.markAsPristine();
          }, 30);
        }
        break;

      case 'dropoff':
        this.form.patchValue({
          dropoffAddress: {
            ...address,
          },
        });
        if (address?.markAsPristine) {
          setTimeout(() => {
            this.form.markAsPristine();
          }, 30);
        }
        break;
    }

    const cond =
      type === 'pickup'
        ? address?.city !== pickupAddress?.city ||
        address?.district !== pickupAddress?.district
        : address?.city !== dropoffAddress?.city ||
        address?.district !== dropoffAddress?.district;

    const newPickupAddress = this.form.get('pickupAddress')?.value;
    const newDropoffAddress = this.form.get('dropoffAddress')?.value;
    if (
      !this.hiddenFields['dropoffAddress'] &&
      newPickupAddress?.city &&
      newPickupAddress?.district &&
      newDropoffAddress?.city &&
      newDropoffAddress?.district &&
      cond
    ) {
      this.regionDistrictService
        .calculateRegionBetweenTwoDistricts({
          fromCity: newPickupAddress.city,
          fromDistrict: newPickupAddress?.district,
          toCity: newDropoffAddress?.city,
          toDisctrict: newDropoffAddress?.district,
        })
        .subscribe({
          next: (res) => {
            this.bridgeInfos = res;
            this.cdr.detectChanges();
            this.cdr.markForCheck();
          },
        });
    } else {
      this.cdr.detectChanges();
      this.bridgeInfos = null;
    }
    if (!address.markAsPristine) {
      this.form.markAsDirty();
    }
  }

  getFormValues() {
    if (this.pickupAddress) {
      if (
        !this.pickupAddress.validateAddressForm() &&
        this.pickupAddress.addressValidator()
      ) {
        return;
      }
    }
    if (this.dropoffAddress) {
      if (
        !this.dropoffAddress.validateAddressForm() &&
        this.dropoffAddress.addressValidator()
      ) {
        return;
      }
    }
    return this.form.getRawValue();
  }

  updateHiddenFields(): void {
    for(const key  of  Object.keys(this.form.controls)){
      this.hiddenFields[key] = this.blackList.includes(key);
    }
  }

  onInstitutionChange(type: 'pickup' | 'dropoff') {
    if (type === 'pickup') {
      const pickupInstitution = this.institutions.find(
        (e) => e.value === this.form.get('pickupInstitution')?.value
      );
      if (pickupInstitution) {
        this.pickupAddress?.setAddress(pickupInstitution?.address);
      } else {
        this.pickupAddress?.resetForm();
      }
    }

    if (type === 'dropoff') {
      const institution = this.institutions.find(
        (e) => e.value === this.form.get('dropoffInstitution')?.value
      );
      if (institution) {
        this.dropoffAddress?.setAddress(institution?.address);
      } else {
        this.dropoffAddress?.resetForm();
      }
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: Event): void {
    this.unSub();
  }

  unSub() {
    if (this.formSubscriptions) {
      this.formSubscriptions.unsubscribe();
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.unSub();
  }
}
